import React from 'react';
import { connect } from 'react-redux';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle, Tooltip } from '@fluentui/react-components';
import { Attach20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { Service } from '../../../models/Entities/Service';
import ServiceReceiptCountPill from './ServiceReceiptCountPill.Component';
import ServiceReceiptsDrawer from './ServiceReceiptsDrawer.Component';
import { useServiceReceiptsStyles } from './ServiceReceipts.Styles';
import {
  ServiceReceiptsConnectedProps,
  ServiceReceiptsDispatchProps,
  getServiceReceiptCount,
  getServiceReceiptTarget,
  isReceiptMissing,
  mapReceiptsDispatchToProps,
  mapReceiptsStateToProps,
} from './ServiceReceipts.Functions';

export interface ServiceReceiptsSectionOwnProps {
  /** Saved service being edited (the receipts belong to its saved protocol) */
  service: Service;
  /** Current value of the "Adjunta comprobante" checkbox in the form */
  attachReceipt: boolean;
}

export type ServiceReceiptsSectionProps = ServiceReceiptsSectionOwnProps &
  ServiceReceiptsConnectedProps &
  ServiceReceiptsDispatchProps;

/** "Comprobantes" section of the service form (only when editing an existing service). */
export const ServiceReceiptsSectionComponent = (props: ServiceReceiptsSectionProps) => {
  const { t } = useTranslation();
  const styles = useServiceReceiptsStyles();
  const [open, setOpen] = React.useState(false);
  const { service, protocolSponsors, ResolveProtocolSponsor } = props;

  React.useEffect(() => {
    if (service.protocolId && protocolSponsors[service.protocolId] === undefined) {
      ResolveProtocolSponsor(service.protocolId);
    }
  }, [service.protocolId, protocolSponsors, ResolveProtocolSponsor]);

  const { target, unavailableReasonKey } = getServiceReceiptTarget(service, protocolSponsors);
  const count = getServiceReceiptCount(service);
  const reason = unavailableReasonKey ? t(unavailableReasonKey) : undefined;

  return (
    <GuideDrawerSection
      className={styles.section}
      testId="service-receipts-section"
      title={
        <span className={styles.sectionTitle}>
          {t('serviceReceipts.sectionTitle')}
          <ServiceReceiptCountPill count={count} />
        </span>
      }
      description={reason ?? t('serviceReceipts.sectionHelp')}
      actions={
        <Tooltip content={reason ?? t('serviceReceipts.manageTooltip')} relationship="description" withArrow>
          <Button
            appearance="secondary"
            icon={<Attach20Regular />}
            onClick={() => setOpen(true)}
            disabledFocusable={!target}
            data-testid="service-receipts-manage"
          >
            {t('serviceReceipts.manage')}
          </Button>
        </Tooltip>
      }
    >
      {isReceiptMissing(props.attachReceipt, count) && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="service-receipts-required">
          <MessageBarBody>
            <MessageBarTitle>{t('serviceReceipts.requiredTitle')}</MessageBarTitle>
            {t('serviceReceipts.requiredMessage')}
          </MessageBarBody>
          {target && (
            <MessageBarActions>
              <Button appearance="transparent" size="small" onClick={() => setOpen(true)}>
                {t('serviceReceipts.attachNow')}
              </Button>
            </MessageBarActions>
          )}
        </GuideMessageBar>
      )}
      <ServiceReceiptsDrawer open={open} onDismiss={() => setOpen(false)} service={service} target={target} />
    </GuideDrawerSection>
  );
};

export default connect(
  mapReceiptsStateToProps,
  mapReceiptsDispatchToProps
)(ServiceReceiptsSectionComponent as React.ComponentType<ServiceReceiptsSectionProps>);
